import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { listStudents } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { TARGET_TYPES } from "./constants";
import {
  Users,
  User,
  Globe,
  UserCheck,
  Search,
  Loader,
  CheckCircle2,
} from "lucide-react";

const NIVEAU_COLORS = {
  gs: "bg-pink-100 text-pink-700",
  cp: "bg-blue-100 text-blue-700",
  ce1: "bg-green-100 text-green-700",
  ce2: "bg-yellow-100 text-yellow-700",
  cm1: "bg-orange-100 text-orange-700",
  cm2: "bg-red-100 text-red-700",
};

function StudentItem({ student, selected, onToggle }) {
  const colors = NIVEAU_COLORS[student.niveau] || NIVEAU_COLORS.cp;
  
  return (
    <div
      className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
        selected 
          ? "border-indigo-500 bg-indigo-50" 
          : "border-slate-200 hover:border-slate-300 bg-white"
      }`}
      onClick={() => onToggle(student.id)}
    >
      <Checkbox
        checked={selected}
        onCheckedChange={() => onToggle(student.id)}
        className="pointer-events-none"
      />
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-slate-800 truncate">{student.student_name}</p>
        <div className="flex items-center gap-2 mt-1">
          <Badge variant="outline" className={`text-xs ${colors}`}>
            {student.niveau?.toUpperCase()}
          </Badge>
          {student.base_profile_id && (
            <Badge variant="secondary" className="text-xs">
              Profil actif
            </Badge>
          )}
        </div>
      </div>
      {selected && <CheckCircle2 className="w-5 h-5 text-indigo-500" />}
    </div>
  );
}

export function TargetAssignmentPanel({ 
  open, 
  onOpenChange, 
  currentTarget,
  onTargetChange,
  onGenerateVariants 
}) {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [targetType, setTargetType] = useState(currentTarget?.type || TARGET_TYPES.GENERIC);
  const [selectedStudentIds, setSelectedStudentIds] = useState(currentTarget?.studentIds || []);
  const [searchQuery, setSearchQuery] = useState("");
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (open && user?.role === "teacher") {
      fetchStudents();
    }
  }, [open, user]);

  useEffect(() => {
    if (currentTarget) {
      setTargetType(currentTarget.type || TARGET_TYPES.GENERIC);
      setSelectedStudentIds(currentTarget.studentIds || []);
    }
  }, [currentTarget]);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const { data } = await listStudents();
      setStudents(data);
    } catch (err) {
      console.error("Error fetching students:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStudent = (studentId) => {
    setSelectedStudentIds(prev => {
      if (prev.includes(studentId)) {
        return prev.filter(id => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  const handleSelectAll = () => {
    const filteredIds = filteredStudents.map(s => s.id);
    setSelectedStudentIds(filteredIds);
  };

  const handleDeselectAll = () => {
    setSelectedStudentIds([]);
  };

  const handleApply = () => {
    const newTarget = {
      type: targetType,
      studentIds: targetType === TARGET_TYPES.GENERIC ? [] : selectedStudentIds,
    };
    onTargetChange(newTarget);
    onOpenChange(false);
  };

  const handleGenerateAndClose = async () => {
    if (selectedStudentIds.length === 0) return;
    
    setGenerating(true);
    try {
      await onGenerateVariants(selectedStudentIds);
      onOpenChange(false);
    } catch (err) {
      console.error("Error generating variants:", err);
    } finally {
      setGenerating(false);
    }
  };

  const filteredStudents = students.filter(s => 
    s.student_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedStudents = students.filter(s => selectedStudentIds.includes(s.id));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-indigo-500" />
            Cible pedagogique
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-4 py-4">
          {/* Target Type Selection */}
          <div>
            <label className="text-sm font-medium text-slate-600 mb-2 block">
              Type de destination
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setTargetType(TARGET_TYPES.GENERIC)}
                className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                  targetType === TARGET_TYPES.GENERIC
                    ? "border-indigo-500 bg-indigo-50"
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <Globe className="w-5 h-5 text-slate-500" />
                <div className="text-left">
                  <p className="font-medium text-sm">Generique</p>
                  <p className="text-xs text-slate-400">Sans differentiation</p>
                </div>
              </button>
              
              <button
                onClick={() => setTargetType(TARGET_TYPES.MULTI_STUDENT)}
                className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                  targetType === TARGET_TYPES.MULTI_STUDENT || targetType === TARGET_TYPES.STUDENT
                    ? "border-indigo-500 bg-indigo-50"
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <UserCheck className="w-5 h-5 text-indigo-500" />
                <div className="text-left">
                  <p className="font-medium text-sm">Eleves cibles</p>
                  <p className="text-xs text-slate-400">Avec differentiation</p>
                </div>
              </button>
            </div>
          </div>

          {/* Student Selection (only if not generic) */}
          {targetType !== TARGET_TYPES.GENERIC && (
            <>
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Rechercher un eleve..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Selection actions */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">
                  {selectedStudentIds.length} eleve(s) selectionne(s)
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleSelectAll}
                  >
                    Tout selectionner
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDeselectAll}
                  >
                    Deselectionner
                  </Button>
                </div>
              </div>

              {/* Student List */}
              <div className="flex-1 overflow-y-auto space-y-2 min-h-[200px] max-h-[300px]">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader className="w-6 h-6 animate-spin text-indigo-500" />
                  </div>
                ) : filteredStudents.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p>Aucun eleve trouve</p>
                  </div>
                ) : (
                  filteredStudents.map((student) => (
                    <StudentItem
                      key={student.id}
                      student={student}
                      selected={selectedStudentIds.includes(student.id)}
                      onToggle={handleToggleStudent}
                    />
                  ))
                )}
              </div>

              {/* Selected preview */}
              {selectedStudentIds.length > 0 && (
                <div className="bg-indigo-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-indigo-700 mb-2">
                    Eleves selectionnes :
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {selectedStudents.slice(0, 5).map((s) => (
                      <Badge key={s.id} variant="secondary" className="text-xs">
                        {s.student_name}
                      </Badge>
                    ))}
                    {selectedStudents.length > 5 && (
                      <Badge variant="outline" className="text-xs">
                        +{selectedStudents.length - 5} autres
                      </Badge>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          
          {targetType !== TARGET_TYPES.GENERIC && selectedStudentIds.length > 0 && (
            <Button
              onClick={handleGenerateAndClose}
              disabled={generating}
              className="bg-green-600 hover:bg-green-700"
            >
              {generating ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  Generation...
                </>
              ) : (
                <>
                  <UserCheck className="w-4 h-4 mr-2" />
                  Generer les versions
                </>
              )}
            </Button>
          )}
          
          <Button onClick={handleApply}>
            Appliquer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Compact version for toolbar
export function TargetIndicator({ target, onClick }) {
  if (!target || target.type === TARGET_TYPES.GENERIC) {
    return (
      <button
        onClick={onClick}
        className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-full text-sm transition-colors"
      >
        <Globe className="w-4 h-4 text-slate-500" />
        <span className="text-slate-600">Generique</span>
      </button>
    );
  }

  const count = target.studentIds?.length || 0;
  
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 px-3 py-1.5 bg-indigo-100 hover:bg-indigo-200 rounded-full text-sm transition-colors"
    >
      <UserCheck className="w-4 h-4 text-indigo-600" />
      <span className="text-indigo-700 font-medium">
        {count} eleve{count > 1 ? "s" : ""}
      </span>
    </button>
  );
}
